<template>
  <div>
    <!-- Mientras carga: solo skeleton, sin acciones (es transitorio) -->
    <div v-if="loadingPictogram" class="rounded-xl border border-gray-200 bg-white shadow-sm p-4">
      <b-skeleton :active="true" :count="5" height="40px"></b-skeleton>
    </div>

    <!-- Estado de error: empty-state pattern — ícono neutro, heading + descripción,
         dos acciones (reintentar como secundaria, volver al listado como primaria). -->
    <div v-else-if="!pictogram"
      class="flex flex-col items-center justify-center text-center p-12 rounded-xl border border-gray-200 bg-white shadow-sm">
      <div class="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-5">
        <b-icon pack="fas" icon="circle-exclamation" size="is-medium" class="text-gray-400"></b-icon>
      </div>
      <h3 class="text-base font-semibold text-gray-900 mb-2">
        {{ $t('pictogram_not_loaded_title') }}
      </h3>
      <p class="text-sm text-gray-500 max-w-sm mb-6">
        {{ $t('pictogram_not_loaded_description') }}
      </p>
      <div class="flex items-center gap-2">
        <b-button type="is-light" size="is-small" icon-pack="fas" icon-left="redo" @click="_loadPictogram">
          {{ $t('retry') }}
        </b-button>
        <b-button type="is-primary" size="is-small" icon-pack="fas" icon-left="arrow-left" @click="_HandleOnClickBack">
          {{ $t('back_to_pictograms') }}
        </b-button>
      </div>
    </div>

    <!-- Contenido normal: header con título a la izquierda + Volver/dropdown a la derecha -->
    <transition v-else
      name="custom-classes-transition"
      enter-active-class="animated fadeInUp"
      leave-active-class="animated fadeOutDown"
    >
      <div class="relative flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm p-4">
        <!-- Overlay de recálculo: cubre todo el panel mientras se recalcula el progreso
             (una sola fuente de verdad `recalculating`, disparada por cualquiera de los
             dos botones de recalcular). -->
        <b-loading :is-full-page="false" :active="recalculating" :can-cancel="false">
          <div class="flex flex-col items-center gap-4">
            <b-icon pack="fas" icon="circle-notch" custom-class="fa-spin" custom-size="fa-3x" class="text-blue-light" />
            <p class="text-lg font-semibold text-blue-dark">{{ $t('recalculating_progress') }}</p>
          </div>
        </b-loading>

        <!-- Header -->
        <div class="flex justify-between items-start mb-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-900">
              {{ $t('pictogram') }}: {{ pictogram.name }}
            </h2>
            <p class="text-sm text-gray-500">{{ assignedToText }}</p>
          </div>

          <div class="flex items-center gap-2">
            <b-button
              type="is-light"
              size="is-small"
              icon-left="arrow-left"
              icon-pack="fas"
              @click="_HandleOnClickBack"
            >
              {{ $t('back_to_pictograms') }}
            </b-button>

            <b-dropdown aria-role="list"
              position="is-bottom-left"
              trap-focus
            >
              <template #trigger="{ active }">
                <b-button
                  :icon-right="active ? 'ellipsis-h' : 'ellipsis-v'"
                  icon-pack="fas"
                  size="is-small"
                />
              </template>

              <b-dropdown-item aria-role="listitem" @click="_HandleClickedRecalculateProgress">
                <b-icon icon="calculator" pack="fas"/>
                {{ $t('recalculate_progress') }}
              </b-dropdown-item>
              <!-- Botón "Eliminar" oculto: no había acción cableada (@click) ni endpoint de
                   borrado de pictograma en backend, así que sólo confundía. Reactivar cuando
                   exista el flujo de borrado (endpoint + policy + confirmación). -->
              <!-- <b-dropdown-item aria-role="listitem" separator></b-dropdown-item>
              <b-dropdown-item aria-role="listitem">
                <b-icon pack="fas" icon="trash-alt"></b-icon>
                {{ $t('delete') }}
              </b-dropdown-item> -->
            </b-dropdown>
          </div>
        </div>

        <!-- Stats Section -->
        <progress-summary :ref="'refProgressSummary'"
          :pictogram="pictogram"
          :assigned-to="assignedTo"
          :recalculating="recalculating"
          @epc-recalculate="_HandleClickedRecalculateProgress"
        />

        <!-- Tabs internos -->
        <div class="flex justify-center gap-3 border-t pt-1 mt-2 dark:border-neutral-700">
          <div class="w-full mt-3">
            <b-tabs type="is-boxed" v-model="activeTab">

              <b-tab-item :label="$t('pictogram')" icon="gear" icon-pack="fas">
                <list-navigation-view ref="refNavPictogram"
                  :items="itemsNavigationPictogram"
                  @epc-pictograms-reload="_HandlePictogramsReload"
                />
              </b-tab-item>

              <b-tab-item :label="$t('layers')" icon="layer-group" icon-pack="fas">
                <list-navigation-view ref="refNavLayers"
                  :items="itemsNavigationLayers"
                  @epc-pictograms-reload="_HandlePictogramsReload"
                />
              </b-tab-item>
            </b-tabs>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ProgressSummary from "./components/progress-summary/progress-summary.vue";
import ListNavigationView from "./components/list-navigation/list-navigation-view.vue";

import { mapLayersLegacy as mapLayers, getListItemsNavigation } from "./services/layers.service.js";
import {
  getPictogram,
  getListItemsNavigation as getListItemsNavigationPictogram,
  recalculateProgress } from "./services/pictograms.service.js";
import { applyReloadPayload } from "./services/panel-refresh.service.js";


export default {
  name: "pictogram-admin-panel",

  components: {
    ProgressSummary,
    ListNavigationView
  },

  data: () => ({
    assignedTo: null, // tiene los datos de la actividad a la que se asigna el pictograma
    assignedToText: null, // usado para mostrar el texto de la actividad a la que se asigna el pictograma

    loadingPictogram: false, // usado para mostrar el skeleton del pictograma
    recalculating: false, // overlay a nivel de todo el panel mientras se recalcula el progreso
    pictogram: null,
    layers: [],
    itemsNavigationLayers: [],
    itemsNavigationPictogram: [],

    activeTab: 0, // Tab activo para resetear navegación al cambiar
  }),

  props: {
    // projectId: { type: Number, required: true },
    // showDownloadFileOriginal: { type: Boolean, default: true },
    // permittedActions: { type:Object, default() { return {} } }, // acciones que se mostraran en la columna acciones por defecto todas
    // pictogram: { type: Object, default() { return null } }, // Pictograma que se esta gestionando
    pictogramId: { type: Number, required: true }, // Pictograma que se esta gestionando
    // canActionsPictogram: { type:Object, default() { return {} } } // acciones que se puede realizar sobre el pictograma por defecto ninguna
  },

  provide() {
    return {
      // Función que retorna el pictograma reactivo. Permite que descendientes
      // (ej. LayerBulkActions dentro del navigation tree de cada capa) accedan
      // a TODAS las layers del pictograma sin pasar props por la cadena.
      // Se llama como this.CurrentPictogram() desde el inject.
      CurrentPictogram: () => this.pictogram,
    };
  },

  watch: {
    pictogramId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this._loadPictogram();
        }
      }
    },
    activeTab() {
      if (this.$refs.refNavPictogram) {
        this.$refs.refNavPictogram.resetNavigation();
      }
      if (this.$refs.refNavLayers) {
        this.$refs.refNavLayers.resetNavigation();
      }
    }
  },

  methods: {
    getAssignedTo() {
      const activities = this.pictogram?.activities;
      const defaultText = this.$t('pictogram_not_assigned');

      // verificamos si hay actividades asignadas al pictograma
      if (activities && activities.length === 0) {
        this.assignedToText = defaultText;
        return;
      }

      // obtenemos la primera actividad
      const firstActivity = activities[0];
      if (!firstActivity) {
        this.assignedToText = defaultText;
        return;
      }

      const { parent_id, name, progress } = firstActivity;
      const entityType = this.$t(parent_id ? 'subactivity' : 'activity');
      this.assignedTo = { entityType, name, progress };

      this.assignedToText = this.$t('pictogram_assigned_to', { entityType, name, progress });
    },

    // showSkeleton=false permite recargar los datos sin mostrar el skeleton (p. ej. tras
    // recalcular, donde ya se muestra el overlay `recalculating` y no queremos el flash).
    // rebuildNav=false refresca el pictograma (y con ello el progress-summary, que es
    // reactivo por props) SIN reconstruir los árboles de navegación. Así, tras un
    // autosave de una hoja, no se resetea la navegación ni se desmonta el editor abierto.
    async _loadPictogram(showSkeleton = true, rebuildNav = true) {
      if (showSkeleton) this.loadingPictogram = true;
      try {
        this.pictogram = await getPictogram(this.pictogramId);
        if (this.pictogram) {
          this.getAssignedTo();
          if (rebuildNav) {
            this.layers = mapLayers(this.pictogram.layers);
            this.itemsNavigationLayers = getListItemsNavigation(this.layers, this.pictogram.type);
            this.itemsNavigationPictogram = getListItemsNavigationPictogram(this.pictogram);
          }
        }
      } catch (error) {
        // Si la carga falla, dejamos pictogram=null para que se muestre el empty-state
        // (y no un skeleton infinito).
        console.error('Error al cargar el pictograma:', error);
        this.pictogram = null;
      } finally {
        if (showSkeleton) this.loadingPictogram = false;
      }
    },

    // Handler único de recálculo: lo disparan tanto el dropdown del header como el botón
    // del progress-summary (vía @epc-recalculate). Muestra el overlay en todo el panel,
    // recalcula, y recarga el pictograma para reflejar progreso/capas/metadata nuevos.
    async _HandleClickedRecalculateProgress() {
      if (this.recalculating) return; // evita disparos dobles
      this.recalculating = true;
      try {
        const response = await recalculateProgress(this.pictogram.id);
        if (response.success) {
          await this._loadPictogram(false); // recarga silenciosa (mantiene el overlay)
          this.$toast.open({
            message: this.$t("recalculate_progress_success"),
            type: "is-success",
            position: "is-top-right",
          });
        } else {
          this.$notify.error(response.message);
        }
      } catch (error) {
        this.$notify.error(this.$t("recalculate_progress_error"));
      } finally {
        this.recalculating = false;
      }
    },

    // Handler del evento `epc-pictograms-reload` emitido por los editores hoja.
    // - Sin payload (editor no migrado): refresh silencioso, sin skeleton ni reset de
    //   navegación (fallback seguro).
    // - Con payload: intenta una actualización PARCIAL in-place (service). Si el cambio
    //   afecta al progreso, además refresca (silencioso, sin reconstruir la navegación).
    _HandlePictogramsReload(payload) {
      if (!payload) {
        this._loadPictogram(false, false);
        return;
      }

      const { patched, needsProgressRefresh } = applyReloadPayload(this.pictogram, payload);

      if (!patched) {
        // Scope desconocido o capa no encontrada → fallback seguro.
        this._loadPictogram(false, false);
        return;
      }

      // Recalcular el texto derivado (actividad/progreso) por si cambió nombre/asignación.
      this.getAssignedTo();

      // Sólo re-fetch cuando el cambio recalcula progreso (refresca el summary),
      // manteniendo navegación y editor abierto.
      if (needsProgressRefresh) {
        this._loadPictogram(false, false);
      }
    },

    _HandleOnClickBack(refresh = false) {
      this.$emit('epc-go-back', {refresh})
    },
  }
};
</script>

<style scoped>
</style>
